import {observer} from "mobx-react";
import {useContext} from "react";
import {Button, IconButton} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {Frame} from "../../common/Frame";
import {LessonEditorStoreContext} from "../../../stores/LessonEditorStore";
import {AppContext} from "../../../stores/AppStore";
import {lessons} from "../../../proto/compiled";
import {TextBlockEditor} from "./TextBlockEditor";
import {AnnotationBlockEditor} from "./AnnotationBlockEditor";

interface ContentBlockEditorProps {
  block: lessons.IContentBlock;
  chapterId: string;
}

export const ContentBlockEditor = observer(({block, chapterId}: ContentBlockEditorProps) => {
  const store = useContext(LessonEditorStoreContext);
  const app = useContext(AppContext);

  if (!store || !app) return null;

  const isEditing = store.editingBlockId === block.id;

  const handleDelete = () => {
    app.confirmationDialogAction = async () => {
      if (block.id) {
        await store.deleteContentBlock(chapterId, block.id);
      }
    };
    app.confirmationDialogText = 'Are you sure you want to delete this content block?';
  };

  const handleStartEdit = () => {
    store.editingBlockId = block.id ?? undefined;
  };

  const handleSave = async () => {
    await store.updateContentBlock(block);
    store.editingBlockId = undefined;
  };

  const isTextBlock = block.blockType === lessons.ContentBlockType.CONTENT_BLOCK_TEXT;
  const isAnyBlockEditing = !!store.editingBlockId;

  return (
    <Frame noTransition row alignItems="flex-start" gap={1}>
      <Frame noTransition grow direction="column" width={0}>
        {isTextBlock ? (
          <TextBlockEditor
            block={block}
            isEditing={isEditing}
          />
        ) : (
          <AnnotationBlockEditor
            block={block}
          />
        )}
      </Frame>

      {isEditing ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={store.saving}
        >
          Save
        </Button>
      ) : !isAnyBlockEditing && (
        <Frame noTransition row gap={0.5}>
          {isTextBlock && (
            <IconButton size="small" onClick={handleStartEdit}>
              <EditIcon fontSize="small"/>
            </IconButton>
          )}
          <IconButton size="small" color="error" onClick={handleDelete}>
            <DeleteIcon fontSize="small"/>
          </IconButton>
        </Frame>
      )}
    </Frame>
  );
});
